import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { map, Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  CreateOrdersRequest,
  Order,
  OrderLight,
  OrderUpdate
} from "src/app/backend/models/order";
import { HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID } from "src/app/core/http-context-tokens";
import {
  OrderService,
  OrderServiceLight
} from "src/app/backend/models/order-service";
import { Physician } from "src/app/backend/models/physician";
import { isUndefined, omitBy, orderBy } from "lodash";

@Injectable()
export class OrderBackend {
  readonly #http = inject(HttpClient);

  load(id: string, light?: boolean): Observable<Order> {
    return this.#http.get<Order>(
      `${environment.snoots.api.store}/orders/${id}`,
      {
        params: light ? { light: "true" } : {}
      }
    );
  }

  loadLight(id: string): Observable<OrderLight> {
    return this.#http.get<OrderLight>(
      `${environment.snoots.api.store}/orders/${id}`,
      {
        params: { light: true }
      }
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<Order>> {
    return this.#http.get<PaginatedResult<Order>>(
      `${environment.snoots.api.store}/orders`,
      {
        params: {
          p: request.page?.toString() ?? "",
          s: request.pageSize?.toString() ?? "",
          sort: "desc",
          orderBy: "createdAt",
          ...request.filterValues
        }
      }
    );
  }

  createOrders(createOrdersRequest: CreateOrdersRequest): Observable<Order[]> {
    return this.#http.post<Order[]>(
      `${environment.snoots.api.store}/orders`,
      createOrdersRequest,
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }

  @UserNotify({
    successMessage: marker("Ordine aggiornato"),
    errorMessage: marker("Impossibile aggiornare l'ordine")
  })
  update(id: string, payload: OrderUpdate): Observable<void> {
    return this.#http.patch<void>(
      `${environment.snoots.api.store}/orders/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Ordine eliminato"),
    errorMessage: marker("Impossibile eliminare l'ordine")
  })
  delete(id: string): Observable<void> {
    return this.#http.delete<void>(
      `${environment.snoots.api.store}/orders/${id}`
    );
  }

  loadServiceLight(id: string): Observable<OrderServiceLight> {
    return this.#http.get<OrderServiceLight>(
      `${environment.snoots.api.store}/services/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  listAllServices(): Observable<OrderService[]> {
    return this.#http
      .get<PaginatedResult<OrderService>>(
        `${environment.snoots.api.store}/services`,
        {
          params: {
            domain: "68c2a0cfef1af3775013385a"
          }
        }
      )
      .pipe(map((response: PaginatedResult<OrderService>) => response.docs));
  }

  loadPhysicians(
    providerId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Physician>> {
    return this.#http.get<PaginatedResult<Physician>>(
      `${environment.snoots.api.iam}/public/tenants/${providerId}/physicians`,
      {
        params: <any>omitBy(
          {
            p: request.page,
            s: request.pageSize,
            orderBy: "firstName",
            sort: "asc",
            ...request.filterValues
          },
          isUndefined
        )
      }
    );
  }

  getPhysician(id: string): Observable<Physician> {
    return this.#http.get<Physician>(
      `${environment.snoots.api.iam}/public/members/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Ordini esportate in Excel"),
    errorMessage: marker("Impossibile esportare gli ordini")
  })
  export(exportOptions: {
    filter?: {
      status?: string;
      serviceId?: string;
      createdAtFrom?: Date;
      createdAtTo?: Date;
      completedAtFrom?: Date;
      completedAtTo?: Date;
    };
    includeIds?: string[];
    excludeIds?: string[];
  }): Observable<any> {
    return this.#http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.store}/orders/exports`,
      {
        ...exportOptions
      }
    );
  }
}
